import { isEqual } from 'lodash-es';
import config from '~/lib/filter-params/config';
import type { ListQuery, ListQueryVariables } from '~/lib/goliath/goliath.types';
import type { Category } from '~/stores/navigation';
import { serializeFilterParams } from '~/lib/filter-params';
import type { FilterParam } from '~/components/filter/filter-types';

export const getUpperCategoryPath = (path: string) =>
  path.split(config.separators.path).slice(0, -1).join(config.separators.path);

// We have in the Store all Goliath Categories. So the propsedPath should be in the list, otherwise the Category dies not exists.
const isPathInNavigation = (proposedPath: string, navigationPaths: Category[]): boolean =>
  navigationPaths.some((category) => {
    if (category.path === proposedPath || category.mappingPath === proposedPath) {
      return true;
    }
    if (category.children) {
      return category.children.some((child) => child.path === proposedPath);
    }
    return false;
  });

export const getRedirectPath = (
  ViewFilter: FilterParam | undefined,
  navigationPaths: Category[]
) => {
  if (!ViewFilter) return '/';
  if (!ViewFilter.productCategoryPaths || ViewFilter.productCategoryPaths.length === 0) {
    return '/';
  }
  const productCategoryPath = ViewFilter.productCategoryPaths[0];
  const proposedPath =
    ViewFilter.brandSlugs && ViewFilter.brandSlugs.length > 0
      ? productCategoryPath
      : getUpperCategoryPath(productCategoryPath);

  return isPathInNavigation(proposedPath, navigationPaths) ? `/${proposedPath}` : '/';
};

/**
 * When we have such an url:
 * "/zubehoer/halterungen+lader-kabel-adapter+schutzfolien-schutzglaeser"
 * Goliath searches for Products in these 3 categories.
 * Goliath returns in the aggregations ony categories with products inside.
 * To prevent a deadlock for the next filter, we remove all categories without any product.
 */

export const checkCategoryFilters = (
  query: ListQueryVariables,
  offers: ListQuery['offers'] | undefined
) => {
  if (!offers) return undefined;
  const { aggregations, variantsCount } = offers;
  if (!variantsCount || variantsCount === 0) return undefined;
  if (!query.filter?.productCategoryPaths || query.filter.productCategoryPaths.length === 0) {
    return undefined;
  }
  // get all possible categories goliath provided
  const responseProductCategoryPaths =
    aggregations?.productCategories?.flatMap((category) => [
      category?.productCategory?.path,
      ...(category?.children || []).map((child) => child?.productCategory?.path),
    ]) || [];

  // Only keep paths that exist in responseProductCategoryPaths
  const newProductCategoryPaths = query.filter.productCategoryPaths.filter((path) =>
    responseProductCategoryPaths.includes(path)
  );
  // when there is no category, we do not want to redirect here.
  if (newProductCategoryPaths.length === 0) return undefined;
  if (!isEqual(query.filter.productCategoryPaths, newProductCategoryPaths)) {
    return serializeFilterParams({
      ...query,
      filter: {
        ...query.filter,
        productCategoryPaths: newProductCategoryPaths,
      },
    } as ListQueryVariables);
  }
  return undefined;
};

export default getRedirectPath;
